import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User as FirebaseUser,
  type UserCredential,
} from 'firebase/auth';
import { auth } from './firebase';

import type { UserProfile } from '../../../../shared/src';
import { getUserProfileEnhancedFallback } from './auth-system-users';

export type { UserProfile };

// Google Auth Provider configuration
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Configure Google provider for Google Workspace
googleProvider.setCustomParameters({
  hd: import.meta.env.VITE_GOOGLE_WORKSPACE_DOMAIN || undefined, // Optional: restrict to specific domain
  prompt: 'select_account',
});

/**
 * Sign in with Google using popup
 */
export const signInWithGoogle = async (): Promise<UserCredential> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    
    // Create or update user profile in Firestore
    await createOrUpdateUserProfile(result.user);
    
    return result;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Create or update user profile in Firestore (hybrid approach)
 */
const createOrUpdateUserProfile = async (user: FirebaseUser): Promise<UserProfile> => {
  try {
    // Use the new auth system with enhanced fallback
    const userProfile = await getUserProfileEnhancedFallback(user.uid, user.email || undefined);
    
    if (userProfile) {
      console.log('✅ User profile found in new auth system:', userProfile);
      return userProfile;
    } else {
      // If user not found in any system, create a basic profile
      console.log('⚠️ User not found in any system, creating basic profile for:', user.uid);
      return {
        uid: user.uid,
        employee_id: 0, // Placeholder - needs to be associated with system_users
        email: user.email || '',
        name: user.displayName || 'New User',
        team_name: 'Unknown Team',
        role_name: 'Unknown Role',
        role: 'contributor',
        lastActive: new Date(),
      };
    }
  } catch (error) {
    console.error('Error creating/updating user profile:', error);
    throw error;
  }
};


/**
 * Auth state change listener
 */
export const onAuthStateChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

